import { Button, Layout, message, Row, Spin, Typography } from "antd";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useFilePicker } from "use-file-picker";
import ArrowChatIcon from "../../../assets/icons/arrow-chat.svg";
import AudioMediaItemComponent from "../../../components/AudioMediaItemComponent/AudioMediaItemComponent";
import AudioMessageComponent from "../../../components/AudioMessageComponent/AudioMessageComponent";
import AvatarComponent from "../../../components/AvatarComponent";
import ScriptsPickerModal from "../../../components/chatScripts/modals/ScriptsPickerModal/ScriptsPickerModal";
import SelectQuickFileAlbumModal from "../../../components/chatScripts/modals/SelectQuickFileAlbumModal/SelectQuickFileAlbumModal";
import SelectQuickFileMediaModal, {
  QuickFileMedia,
} from "../../../components/chatScripts/modals/SelectQuickFileMediaModal/SelectQuickFileMediaModal";
import ConversationThreeDotsModalComponent from "../../../components/ConversationThreeDotsModalComponent";
import CountDownComponent from "../../../components/CountDownComponent";
import CreatorMessageAcceptMediaModalComponent from "../../../components/CreatorMessageAcceptMediaModalComponent";
import FanAvatarComponent from "../../../components/FanAvatarComponent";
import IncomeMessageComponent from "../../../components/IncomeMessageItemComponent";
import MailboxMediaModal from "../../../components/MailboxMediaModal";
import MessageInputComponent from "../../../components/MessageInputComponent";
import MessageMediaItemComponent from "../../../components/MessageMediaItemComponent";
import MessageTextItemComponent from "../../../components/MessageTextItemComponent";
import RecordAudioModal from "../../../components/RecordAudioModal/RecordAudioModal";
import ShareModalComponent from "../../../components/ShareModalComponent";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import SubscriberBadge from "../../../components/SubscriberBadge/SubscriberBadge";
import ThreeDotsComponent from "../../../components/ThreeDotsComponent";
import {
  ACCEPTED,
  C_CURRENT_TAB,
  CONVERSATION_DATE_FORMAT,
  CREATOR,
  FAN,
  FILE,
  HOURS_TO_EXPIRE,
  REJECTED,
  REQUEST_LOADING_STATES,
  TEXT,
} from "../../../helpers/constant";
import { usePreviousLocationContext } from "../../../helpers/PreviousLocationContext";
import { IMessage, IPersonalizedMediaRequest, ModalMediaType } from "../../../helpers/types";
import { createPersonalizedMedia, setPendingMediaFiles } from "../../../redux/mediaPush/mediaPushSlice";
import {
  addMessageInstant,
  findAllMessagesByConversationId,
  getConversationById,
  readAllMessages,
  readAllMessagesInConversation,
  removeFromUnanswered,
  removeFromUnread,
  sendMessage,
} from "../../../redux/messages/messagesSlice";
import { AppDispatch, StoreState } from "../../../redux/store";
import { authService, fileService, messageService, subscriptionsService } from "../../../services";
import { socket } from "../../../socket";
import "./index.less";

const CreatorConversationPage = () => {
  const { conversationId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const previousLocation = usePreviousLocationContext();
  const selectedConversation = useSelector((state: StoreState) => state.messages.selectedConversation);
  const messageData = useSelector((state: StoreState) =>
    state.messages.messagesData.find((conversation) => conversation.conversationId === conversationId)
  );
  const loadingMore = useSelector((state: StoreState) => state.messages.findAllMessagesByConversationIdLoading);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef<boolean>(false);
  const pageRef = useRef<number>(messageData?.page ? messageData.page : 1);
  const hasMoreRef = useRef<boolean>(true);
  const [progress, setProgress] = useState<number>(0);

  const shouldFetch = useRef(true);
  // useEffect(() => {
  //   loadMessages(1, { initial: true });
  // }, []);

  type LoadOpts = { initial?: boolean; prepend?: boolean };

  const didInitialScrollRef = useRef<boolean>(false);

  const [openMediaModal, setOpenMediaModal] = useState<boolean | false>(false);

  const currentUser = { role: authService.getRole(), userId: authService.getId() };
  const messages = messageData?.messages || [];

  const [isConversationStep, setIsConversationStep] = useState<boolean>((messageData && messageData?.messages.length > 0) || true);

  const { unreadConversations } = useSelector((state: StoreState) => state.messages);
  const preventScrollUpdateRef = useRef<boolean>(false);
  const isUserAtBottomRef = useRef<boolean>(true);
  const scrollAnchorRef = useRef<{ height: number; top: number; offset: number } | null>(null);
  const [isSavedResponsesModalOpen, setIsSavedResponsesModalOpen] = useState<boolean>(false);
  const [quickAnswer, setQuickAnswer] = useState<string>("");

  const [isConversationModalOpen, setIsConversationModalOpen] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isAcceptMediaModalOpen, setIsAcceptMediaModalOpen] = useState<boolean>(false);
  const [messageIdToAccept, setMessageIdToAccept] = useState<string>("");
  const [openFileSelector, { plainFiles, clear }] = useFilePicker({
    readAs: "ArrayBuffer",
    multiple: false,
    accept: ["image/*", "video/*", "audio/*"],
    limitFilesConfig: { max: 1 },
  });
  const [showScriptsModal, setShowScriptsModal] = useState<boolean>(false);
  const [showAudioModal, setShowAudioModal] = useState<boolean>(false);
  const [openAudioImportModal, setOpenAudioImportModal] = useState<boolean>(false);
  const [showQuickAlbumsModal, setShowQuickAlbumsModal] = useState<boolean>(false);
  const [selectedQuickAlbumId, setSelectedQuickAlbumId] = useState<string | null>(null);
  const [selectedAlbumType, setSelectedAlbumType] = useState<"media" | "audio">("media");
  const [quickFileSelectionType, setQuickFileSelectionType] = useState<"media" | "audio">("media");

  let hasNextPage;
  if (!messageData) {
    hasNextPage = true;
  } else {
    hasNextPage = messageData.messages.length > 0 && messageData?.page < messageData?.totalPages;
  }

  useEffect(() => {
    if (shouldFetch.current) {
      shouldFetch.current = false;
      if (conversationId) {
        dispatch(readAllMessages(conversationId));
      }
      dispatch(readAllMessagesInConversation({ conversationId, conversationType: window.localStorage.getItem(C_CURRENT_TAB) }));
      if (isEmpty(selectedConversation) && conversationId !== undefined) {
        dispatch(getConversationById(conversationId));
      }
      // if (!isEmpty(selectedConversation)) {
      //   if (selectedConversation?.interlocutor?.chat !== "free") {
      //     // updateSubscription();
      //   }
      // } else {
      //   if (conversationId !== undefined) {
      //     dispatch(getConversationById(conversationId));
      //   }
      // }
    }
  }, []);

  const loadMessages = useCallback(
    async (pageNum: number, opts: LoadOpts = {}) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);

      const container = messagesContainerRef.current;
      try {
        if (opts.prepend && container) {
          // Add a class to prevent visible scroll changes
          container.classList.add("scroll-lock");

          // Store current scroll anchor before loading new content
          const oldScrollHeight = container.scrollHeight;
          const oldScrollTop = container.scrollTop;
          scrollAnchorRef.current = {
            height: oldScrollHeight,
            top: oldScrollTop,
            offset: oldScrollHeight - oldScrollTop,
          };
        }

        if (hasNextPage) {
          await dispatch(findAllMessagesByConversationId({ conversationId: conversationId!, page: pageNum }));
        }

        // Ensure we remove the scroll-lock class immediately after loading
        if (container && opts.prepend) {
          // Use requestAnimationFrame to ensure the DOM has updated
          requestAnimationFrame(() => {
            if (scrollAnchorRef.current !== null) {
              const newScrollHeight = container.scrollHeight;
              const heightDifference = newScrollHeight - scrollAnchorRef.current.height;
              container.scrollTop = scrollAnchorRef.current.top + heightDifference;
            }
            // Always remove the scroll-lock class
            container.classList.remove("scroll-lock");
          });
        }

        setLoading(false);
        loadingRef.current = false;
      } catch (error) {
        console.error("Error loading messages:", error);
        // Ensure we remove the scroll-lock class even if there's an error
        if (container) {
          container.classList.remove("scroll-lock");
        }
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [conversationId, dispatch, hasNextPage]
  );

  const preserveScrollPosition = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el || preventScrollUpdateRef.current) return;

    if (scrollAnchorRef.current !== null) {
      // Use immediate scroll adjustment
      requestAnimationFrame(() => {
        if (!el || scrollAnchorRef.current === null) return;

        const newScrollHeight = el.scrollHeight;
        const heightDifference = newScrollHeight - scrollAnchorRef.current.height;

        // Set scroll position to maintain the same view the user was seeing
        el.scrollTop = scrollAnchorRef.current.top + heightDifference;
        // Clear the scroll anchor reference
        scrollAnchorRef.current = null;
      });
    } else if (isUserAtBottomRef.current) {
      // Only scroll to bottom if user was already at bottom
      requestAnimationFrame(() => {
        if (!el) return;
        const target = Math.max(0, el.scrollHeight - el.clientHeight);
        el.scrollTop = target;
      });
    }
  }, []);

  useEffect(() => {
    function onProgressUpdate(data: number) {
      setProgress((prev) => {
        const prog = prev + 1;
        if (prog <= 99) {
          return prog;
        }
        return prev;
      });
    }

    socket.on("progress-update", onProgressUpdate);

    return () => {
      socket.off("progress-update", onProgressUpdate);
    };
  }, []);

  useEffect(() => {
    return () => {
      const container = messagesContainerRef.current;
      if (container) {
        container.classList.remove("scroll-lock");
      }
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    preventScrollUpdateRef.current = true;
    requestAnimationFrame(() => {
      const target = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = target;
      isUserAtBottomRef.current = true;
      didInitialScrollRef.current = true;
      preventScrollUpdateRef.current = false;
    });
  }, []);

  useLayoutEffect(() => {
    if (!didInitialScrollRef.current && messages.length > 0) {
      scrollToBottom();
      return;
    }
    preserveScrollPosition();
  }, [messages.length, scrollToBottom, preserveScrollPosition]);

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || preventScrollUpdateRef.current) return;

    if (container.scrollTop < 0) {
      container.scrollTop = 0;
    }

    const distanceFromBottom = container.scrollHeight - (container.scrollTop + container.clientHeight);
    isUserAtBottomRef.current = distanceFromBottom <= 100;
  }, []);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    if (messageData) {
      pageRef.current = messageData.page + 1;
    }
  }, [messageData]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    const root = messagesContainerRef.current;
    const sentinel = topSentinelRef.current;
    if (!root || !sentinel || loading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !loadingRef.current && hasMoreRef.current) {
          loadMessages(pageRef.current, { prepend: true });
        }
      },
      {
        root,
        threshold: 0,
        rootMargin: "50px 0px 0px 0px",
      }
    );

    observer.observe(sentinel);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [loading, hasMore, loadMessages]);

  // useEffect(() => {
  //   if (isEmpty(messageData)) {
  //     console.log("messageData is empty", messageData);
  //     dispatch(findAllMessagesByConversationId({ conversationId: conversationId!, page }));
  //   }
  // }, [messageData]);

  useEffect(() => {
    if (!isEmpty(plainFiles)) {
      const file = plainFiles[0];
      const isAudioFile =
        file.type.startsWith("audio/") || [".mp3", ".wav", ".m4a", ".ogg", ".aac", ".webm"].some((ext) => file.name.toLowerCase().endsWith(ext));

      // Only set global loading for non-audio files
      if (!isAudioFile) {
        setIsLoading(true);
      }

      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      let newMessage: IMessage = {
        temp_id: tempId,
        conversationId: conversationId,
        type: FILE,
        uploading: true,
        medias: [],
      };

      // Mark as audio file if it's an audio file
      if (isAudioFile) {
        (newMessage as any).isAudioFile = true;
      }

      dispatch(addMessageInstant(newMessage));
      dispatch(
        removeFromUnanswered({
          conversationId: selectedConversation?._id!,
          type: window.localStorage.getItem(C_CURRENT_TAB)!,
        })
      );
      dispatch(
        removeFromUnread({
          conversationId: selectedConversation?._id!,
          type: window.localStorage.getItem(C_CURRENT_TAB)!,
        })
      );
      const formData = new FormData();
      formData.append("media", file);
      formData.append("temp_id", tempId);

      if (isAudioFile) {
        formData.append("isAudio", "true");
      }

      fileService
        .uploadMedia(formData, setProgress)
        .then((uploadedFile) => {
          // Create a new copy of the message to avoid modifying immutable Redux state
          const updatedMessage: IMessage = {
            ...newMessage,
            medias: [
              {
                fileId: uploadedFile._id !== undefined ? uploadedFile._id : "",
                isPrivate: false,
              },
            ],
            uploading: false,
          }; // Keep the audio file flag
          if (isAudioFile) {
            (updatedMessage as any).isAudioFile = true;
          }

          dispatch(sendMessage(updatedMessage));
          clear();

          // Only clear global loading for non-audio files
          if (!isAudioFile) {
            setIsLoading(false);
          }
        })
        .catch((error) => {
          console.log(error);
          message.error("Something went wrong, could not send file !");

          // Clear global loading on error for non-audio files
          if (!isAudioFile) {
            setIsLoading(false);
          }
        });
    }
  }, [plainFiles]);

  useEffect(() => {
    if (window.localStorage.getItem("quickFileAudioOnly")) {
      window.localStorage.removeItem("quickFileAudioOnly");
    }
    if (window.localStorage.getItem("quickFileMediaOnly")) {
      window.localStorage.removeItem("quickFileMediaOnly");
    }
  }, []);

  const acceptMessage = useCallback(
    (message: IMessage) => {
      if (currentUser?.role === CREATOR) {
        // Creator accepting a message - open the media modal
        setIsAcceptMediaModalOpen(true);
        setMessageIdToAccept(message._id !== undefined ? message._id : "");
        dispatch(
          removeFromUnanswered({
            conversationId: selectedConversation?._id!,
            type: window.localStorage.getItem(C_CURRENT_TAB)!,
          })
        );
        dispatch(
          removeFromUnread({
            conversationId: selectedConversation?._id!,
            type: window.localStorage.getItem(C_CURRENT_TAB)!,
          })
        );
      } else {
        // Fan accepting a message - check if it's paid content
        if (message.mediaPushPrice !== undefined) {
          // console.log("Message has mediaPushPrice:", message.mediaPushPrice);

          // Only free messages (price === 0) can be accepted directly
          if (message.mediaPushPrice.price === 0) {
            // console.log("Free message, accepting directly");
            messageService
              .updateMessageStatus({ messageId: message._id!, action: ACCEPTED, userType: FAN })
              .then(() => {
                // console.log("Message accepted successfully");
              })
              .catch((error) => {
                console.error("Error accepting message:", error);
              });
          } else {
            // Paid message - redirect to payment page
            // console.log("Paid message, redirecting to payment");
            navigate(
              `/common/payment?creatorId=${message.senderId}&type=mediaPush&mediaPushId=${message.mediaPushId}&messageId=${message._id}&redirectUrl=creator/conversation/${selectedConversation?._id}`
            );
          }
        }
        // else {
        //   console.log("Message has no mediaPushPrice");
        // }
      }
    },
    [currentUser, selectedConversation, navigate, dispatch]
  );

  const rejectMessage = useCallback((messageId: string) => {
    messageService.updateMessageStatus({ messageId, action: REJECTED, userType: CREATOR }).catch(() => {});
  }, []);

  const messageTextSendClicked = useCallback(
    (newMessage: string) => {
      if (isEmpty(newMessage) || newMessage.trim() === "") return;
      const messageToSend: IMessage = {
        temp_id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        conversationId: String(conversationId),
        type: TEXT,
        text: newMessage,
      };

      dispatch(addMessageInstant(messageToSend));
      dispatch(sendMessage(messageToSend));
      dispatch(
        removeFromUnanswered({
          conversationId: selectedConversation?._id!,
          type: window.localStorage.getItem(C_CURRENT_TAB)!,
        })
      );
      dispatch(
        removeFromUnread({
          conversationId: selectedConversation?._id!,
          type: window.localStorage.getItem(C_CURRENT_TAB)!,
        })
      );
      if (quickAnswer !== "") {
        setQuickAnswer("");
      }
    },
    [conversationId, currentUser, selectedConversation, quickAnswer]
  );

  const sendMediaButtonClicked = useCallback(
    (messageId?: string) => {
      navigate(
        `/creator/mediaPush?conversation=${true}&conversationId=${selectedConversation?._id}&receiverId=${selectedConversation?.interlocutor?.userId}&messageId=${messageId}`
      );
    },
    [selectedConversation]
  );

  const selectQuickMessage = useCallback((message: string) => {
    if (message && !isEmpty(message)) {
      setQuickAnswer(message);
    }
  }, []);

  const onPrivateGalleryClick = useCallback(() => {
    let receiverId: string;
    let senderId: string;
    if (selectedConversation?.interlocutor?.role === FAN) {
      receiverId = selectedConversation?.interlocutor?.userId !== undefined ? selectedConversation.interlocutor?.userId : "";
      senderId = currentUser?.userId !== undefined ? currentUser?.userId : "";
    } else {
      receiverId = currentUser?.userId !== undefined ? currentUser?.userId : "";
      senderId = selectedConversation?.interlocutor?.userId !== undefined ? selectedConversation.interlocutor?.userId : "";
    }
    navigate(
      `/common/privateGallery?conversationId=${selectedConversation?._id}&shouldAddWatermark=${selectedConversation?.interlocutor?.role === FAN}`
    );
  }, [selectedConversation, currentUser]);

  const onShareProfileClick = useCallback(() => {
    setIsConversationModalOpen(false);
    setIsShareModalOpen(true);
  }, []);
  async function handleRemoveSubscriber() {
    if (selectedConversation && selectedConversation.interlocutor?.role === FAN) {
      const userId: any = selectedConversation.interlocutor?.userId;
      await subscriptionsService.removeSubscriber(userId).then((res) => {
        if (res?.data) {
          message.success("Fan removed");
          navigate("/creator/message");
        } else {
          message.error(t("something-want-wrong"));
        }
      });
    }
  }

  const submitMedia = useCallback(
    async (message: ModalMediaType) => {
      if (!conversationId || !selectedConversation?.interlocutor?.userId) return;
      let mediaToCreate: IPersonalizedMediaRequest = {
        price: message.customAmount !== "" ? parseFloat(message.customAmount) : parseInt(message.amount, 10),
        text: message.message,
        conversationId: conversationId,
        receivers: [selectedConversation.interlocutor?.userId],
        messageIdToAccept: messageIdToAccept,
      };

      await dispatch(createPersonalizedMedia(mediaToCreate));
      setIsAcceptMediaModalOpen(false);
      setMessageIdToAccept("");
    },
    [conversationId, selectedConversation, messageIdToAccept]
  );

  const handleSendMedia = useCallback(() => {
    openFileSelector();
    setOpenMediaModal(false);
  }, []);

  // Handle audio recording
  const handleAudioRecorded = (audioBlob: Blob, audioTitle: string) => {
    const audioFile = new File([audioBlob], `${audioTitle || "audio_recording"}.webm`, { type: "audio/webm;codecs=opus" });
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const newMessage: IMessage = {
      temp_id: tempId,
      conversationId: String(conversationId),
      type: FILE,
      uploading: true,
      medias: [],
    };

    (newMessage as any).isAudioFile = true;
    dispatch(addMessageInstant(newMessage));
    dispatch(
      removeFromUnanswered({
        conversationId: selectedConversation?._id!,
        type: window.localStorage.getItem(C_CURRENT_TAB)!,
      })
    );
    dispatch(
      removeFromUnread({
        conversationId: selectedConversation?._id!,
        type: window.localStorage.getItem(C_CURRENT_TAB)!,
      })
    );

    const formData = new FormData();
    formData.append("media", audioFile);
    formData.append("temp_id", tempId);
    formData.append("isAudio", "true");

    fileService
      .uploadMedia(formData, setProgress)
      .then((file) => {
        const updatedMessage: IMessage = {
          ...newMessage,
          medias: [
            {
              fileId: file._id !== undefined ? file._id : "",
              isPrivate: false,
            },
          ],
          uploading: false,
        };

        (updatedMessage as any).isAudioFile = true;
        dispatch(sendMessage(updatedMessage));
      })
      .catch((error) => {
        console.error("Audio upload error:", error);
        message.error("Something went wrong, could not send audio file!");

        const errorMessage: IMessage = {
          ...newMessage,
          uploading: false,
        };
        dispatch(sendMessage(errorMessage));
      });

    setShowAudioModal(false);
  };

  // Handle quick files selection from modals
  const handleQuickFilesClick = (type: "media" | "audio") => {
    setQuickFileSelectionType(type);
    setOpenMediaModal(false);
    setOpenAudioImportModal(false);
    setShowQuickAlbumsModal(true);
  };

  const handleQuickFilesConfirm = async (selectedFiles: QuickFileMedia[]) => {
    try {
      for (const item of selectedFiles) {
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const isAudio = item.isAudio;
        const isVideo = item.isVideo;

        const newMessage: IMessage = {
          temp_id: tempId,
          conversationId: String(conversationId),
          type: FILE,
          uploading: false,
          medias: [
            {
              fileId: item.id,
              isPrivate: false,
            },
          ],
        };

        if (isAudio) {
          (newMessage as any).isAudioFile = true;
        }

        dispatch(addMessageInstant(newMessage));

        dispatch(sendMessage(newMessage));
      }

      dispatch(
        removeFromUnanswered({
          conversationId: selectedConversation?._id!,
          type: window.localStorage.getItem(C_CURRENT_TAB)!,
        })
      );
      dispatch(
        removeFromUnread({
          conversationId: selectedConversation?._id!,
          type: window.localStorage.getItem(C_CURRENT_TAB)!,
        })
      );
      setSelectedQuickAlbumId(null);
    } catch (error) {
      console.error("Error sending quick files:", error);
      message.error(t("back-error-message"));
    }
  };

  const handleNavigation = useCallback(() => {
    dispatch(setPendingMediaFiles({ files: [] }));
    if (previousLocation?.pathname !== undefined && previousLocation?.pathname.includes("/p/")) {
      navigate(-1);
    } else {
      navigate("/creator/message");
    }
  }, [previousLocation, navigate]);

  return (
    <Spin spinning={isLoading} style={{ maxHeight: "100%" }} indicator={<SpinnerComponent progress={true} progressValue={progress} />}>
      <Layout className="fan-message-test-container">
        <Layout.Header className="header-chat">
          <div className="header-chat-row">
            <div className="messagerie-header-icon" onClick={handleNavigation}>
              <img width={11} src={ArrowChatIcon} alt="Twizz messages" />
              {unreadConversations > 0 && (
                <Typography.Text className={`notif-message-conv`}>{unreadConversations > 99 ? "99+" : unreadConversations}</Typography.Text>
              )}
            </div>
            <div className="header-content">
              {selectedConversation?.interlocutor?.avatar ? (
                <AvatarComponent image={selectedConversation?.interlocutor?.avatar} size={35} />
              ) : (
                <FanAvatarComponent name={selectedConversation?.interlocutor?.name} size={35} />
              )}
              <div className="header-text-wrapper">
                <Typography.Text className="title-name-messagerie-header text-white-color">
                  {selectedConversation?.interlocutor?.name === "user-not-found" ? t("user-not-found") : selectedConversation?.interlocutor?.name}
                </Typography.Text>
                {/* <VerifiedUserIconComponent
                  userId={selectedConversation?.interlocutor?.userId}
                  verified={selectedConversation?.interlocutor?.isVerified}
                  fetch={false}
                /> 
                {(() => {
                  const isSubscribed = selectedConversation?.isFanSubscribed && new Date(selectedConversation.isFanSubscribed) > new Date();
                  return selectedConversation?.isFanSubscribed !== null &&
                    !isSubscribed &&
                    selectedConversation?.interlocutor?.name === "user-not-found" && (
                      <Button className="fanRequestMediaSubscribeBtn">{t("subscribe")}</Button>
                    );
                })()} */}
                {(() => {
                  const isSubscribed = selectedConversation?.isFanSubscribed && new Date(selectedConversation.isFanSubscribed) > new Date();
                  return isSubscribed && <SubscriberBadge />;
                })()}
              </div>
            </div>
            <div className="three-dots-component">
              <ThreeDotsComponent onClick={() => setIsConversationModalOpen(true)} />
            </div>
          </div>
        </Layout.Header>
        <Layout.Content className={`content fanRequestMediaWrapper background-transparent ${loading && "loading"}`}>
          {loadingMore === REQUEST_LOADING_STATES.PENDING && (
            <div
              style={{
                position: "absolute",
                top: "80px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
                backgroundColor: "rgba(0, 0, 0, 0)",
                borderRadius: "50%",
                padding: "5px",
              }}
            >
              <SpinnerComponent size={20} />
            </div>
          )}
          {isConversationStep && (
            <div className="messages-container" ref={messagesContainerRef} onScroll={handleScroll}>
              <div
                ref={topSentinelRef}
                style={{ height: "1px", width: "100%", pointerEvents: "none", opacity: 0, margin: 0, padding: 0, alignSelf: "stretch" }}
              />
              {messages?.map((message, index) => {
                const isIncome = message.receiverId === currentUser?.userId;
                const target = dayjs(message.updatedAt).add(HOURS_TO_EXPIRE, "hours");
                const remainingHours = target.diff(dayjs(), "hours");
                const remainingMinutes = target.diff(dayjs(), "minutes");
                const remainingSeconds = target.diff(dayjs(), "seconds");
                const messageDate = dayjs(message.createdAt);
                const showDate = index === 0 || messageDate.diff(dayjs(messages[index - 1].createdAt), "hour") >= 1;
                return (
                  <React.Fragment key={message._id}>
                    {showDate && (
                      <Row className="justify-content-center mt-10 mb-10">
                        <Typography.Text className="messageDateLabel">{messageDate.format(CONVERSATION_DATE_FORMAT)}</Typography.Text>
                      </Row>
                    )}
                    <Row id={message._id} key={message._id} className={isIncome ? "justify-content-start" : "justify-content-end"}>
                      {message.type === TEXT && !isIncome && <MessageTextItemComponent message={message} userRole={currentUser?.role} />}
                      {message.type === FILE && !isIncome && (
                        <>
                          {message.status === ACCEPTED && isEmpty(message.medias) && !message.uploading ? (
                            <CountDownComponent targetDate={message.updatedAt!} />
                          ) : null}
                          {(() => {
                            // Check if message contains any audio media
                            const hasAudioMedia =
                              message.medias &&
                              message.medias.length === 1 &&
                              !message.mediaPushPrice &&
                              message.medias.some((media) => {
                                return (
                                  media.isAudio === true ||
                                  media.url?.toLowerCase().includes(".mp3") ||
                                  media.url?.toLowerCase().includes(".wav") ||
                                  media.url?.toLowerCase().includes(".m4a") ||
                                  media.url?.toLowerCase().includes(".ogg") ||
                                  media.url?.toLowerCase().includes(".aac") ||
                                  media.url?.toLowerCase().includes(".webm") ||
                                  media.url?.toLowerCase().includes("audio/") ||
                                  media.url?.toLowerCase().includes("audio%2f")
                                );
                              });

                            // Check if message is an audio message
                            const isAudioMessage = hasAudioMedia || (message as any).isAudioFile;

                            if (isAudioMessage) {
                              // If it has mediaPushPrice, treat as premium audio (regardless of other media types)

                              return (
                                <AudioMessageComponent
                                  audioFile={{
                                    _id: message._id || (message.medias && message.medias[0] ? message.medias[0].fileId : ""),
                                    absolutePath: message.medias && message.medias[0] ? message.medias[0].url : "",
                                    name: message.medias && message.medias[0] ? message.medias[0].name || "audio" : "audio",
                                  }}
                                  isOwn={true}
                                  date={dayjs(message.createdAt).format("HH:mm")}
                                  isProcessing={message.uploading || (message as any).isProcessing}
                                  isRead={message.isRead || false}
                                />
                              );
                            } else {
                              // Not an audio message, render as regular media
                              return (
                                <MessageMediaItemComponent
                                  displayVideo={true}
                                  message={message}
                                  expired={
                                    message.status === ACCEPTED &&
                                    isEmpty(message.medias) &&
                                    !(remainingHours > 0) &&
                                    !(remainingMinutes > 0) &&
                                    !(remainingSeconds > 0)
                                  }
                                  isBlur={false}
                                />
                              );
                            }
                          })()}
                        </>
                      )}
                      {isIncome &&
                        // Use the same approach for incoming messages
                        (() => {
                          // Check if message contains any audio media
                          const hasAudioMedia =
                            message.medias &&
                            message.medias.some((media) => {
                              return (
                                media.isAudio === true ||
                                media.url?.toLowerCase().includes(".mp3") ||
                                media.url?.toLowerCase().includes(".wav") ||
                                media.url?.toLowerCase().includes(".m4a") ||
                                media.url?.toLowerCase().includes(".ogg") ||
                                media.url?.toLowerCase().includes(".aac") ||
                                media.url?.toLowerCase().includes(".webm") ||
                                media.url?.toLowerCase().includes("audio/") ||
                                media.url?.toLowerCase().includes("audio%2f")
                              );
                            });

                          // Check if message is an audio message
                          const isAudioMessage = hasAudioMedia || (message as any).isAudioFile;

                          if (isAudioMessage) {
                            // If it has mediaPushPrice, treat as premium audio (regardless of other media types)
                            if (message.mediaPushPrice) {
                              return (
                                <AudioMediaItemComponent
                                  message={message}
                                  hideStatus={false}
                                  isIncome={true}
                                  displayVideo={message.status === ACCEPTED} // Enable playing for accepted messages
                                  acceptMessage={acceptMessage}
                                  rejectMessage={rejectMessage}
                                  user={selectedConversation?.interlocutor?.avatar}
                                  username={selectedConversation?.interlocutor?.username || ""}
                                />
                              );
                            } else {
                              // Regular audio message
                              return (
                                <div className="incomeMessage">
                                  {selectedConversation?.interlocutor?.avatar && (
                                    <AvatarComponent image={selectedConversation.interlocutor.avatar} size={23} />
                                  )}
                                  <AudioMessageComponent
                                    audioFile={{
                                      _id: message._id || (message.medias && message.medias[0] ? message.medias[0].fileId : ""),
                                      absolutePath: message.medias && message.medias[0] ? message.medias[0].url : "",
                                      name: message.medias && message.medias[0] ? message.medias[0].name || "audio" : "audio",
                                    }}
                                    isOwn={false}
                                    date={dayjs(message.createdAt).format("HH:mm")}
                                  />
                                </div>
                              );
                            }
                          } else {
                            // Not an audio message, render as regular message
                            return (
                              <IncomeMessageComponent
                                message={message}
                                user={selectedConversation?.interlocutor?.avatar}
                                userRole={currentUser?.role}
                                acceptMessage={acceptMessage}
                                rejectMessage={rejectMessage}
                                hideStatus={message.type === FILE && selectedConversation?.interlocutor?.role === FAN}
                              />
                            );
                          }
                        })()}
                    </Row>
                  </React.Fragment>
                );
              })}
              <div
                ref={bottomSentinelRef}
                style={{ height: "1px", width: "100%", pointerEvents: "none", opacity: 0, margin: 0, padding: 0, alignSelf: "stretch" }}
              />
            </div>
          )}
          {selectedConversation?.interlocutor?.name === "user-not-found" ? (
            ""
          ) : (
            <>
              {isConversationStep && currentUser?.role === CREATOR && (
                <Row className="message-input-row">
                  <MessageInputComponent
                    onSendClick={messageTextSendClicked}
                    onMediaClick={() => setOpenMediaModal(true)}
                    onChatScriptClick={() => setShowScriptsModal(true)}
                    onAudioClick={() => setShowAudioModal(true)}
                    quickAnswer={quickAnswer}
                  />
                  <Button
                    className="mediaConversationBtn"
                    style={{ width: "fit-content" }}
                    onClick={() => {
                      sendMediaButtonClicked();
                    }}
                  >
                    {t("media")}
                  </Button>
                </Row>
              )}
            </>
          )}
        </Layout.Content>
        {showScriptsModal && <ScriptsPickerModal onClose={() => setShowScriptsModal(false)} />}
        {/* <CreatorSavedResponsesModalComponent
          isOpen={isSavedResponsesModalOpen && selectedConversation?.interlocutor?.name !== "user-not-found"}
          onCloseClick={() => setIsSavedResponsesModalOpen(false)}
          selectMessage={selectQuickMessage}
        /> */}
        <ConversationThreeDotsModalComponent
          isOpen={isConversationModalOpen}
          onClose={() => setIsConversationModalOpen(false)}
          onPrivateGalleryClick={onPrivateGalleryClick}
          onShareProfileClick={onShareProfileClick}
          onCancelSubscriptionClick={handleRemoveSubscriber}
          hideShareButton={selectedConversation?.interlocutor?.role === FAN}
          hideCancelSubButton={selectedConversation?.interlocutor?.role !== FAN}
        />
        <CreatorMessageAcceptMediaModalComponent
          isOpen={isAcceptMediaModalOpen && selectedConversation?.interlocutor?.name !== "user-not-found"}
          onClose={() => {
            setIsAcceptMediaModalOpen(false);
            setMessageIdToAccept("");
          }}
          onSubmit={submitMedia}
        />
        <ShareModalComponent
          isOpen={isShareModalOpen && selectedConversation?.interlocutor?.name !== "user-not-found"}
          onClose={() => setIsShareModalOpen(false)}
          id={selectedConversation?.interlocutor?.userId}
          username={selectedConversation?.interlocutor?.username}
        />
        <MailboxMediaModal
          isOpen={openMediaModal && selectedConversation?.interlocutor?.name !== "user-not-found"}
          onClose={() => setOpenMediaModal(false)}
          onMyDevice={handleSendMedia}
          onMyAlbums={() => handleQuickFilesClick("media")}
        />

        <MailboxMediaModal
          isOpen={openAudioImportModal && selectedConversation?.interlocutor?.name !== "user-not-found"}
          onClose={() => setOpenAudioImportModal(false)}
          onMyDevice={handleSendMedia}
          onMyAlbums={() => handleQuickFilesClick("audio")}
          onRecordAudio={() => {
            setOpenAudioImportModal(false);
            setShowAudioModal(true);
          }}
        />
        <RecordAudioModal
          isOpen={showAudioModal}
          onClose={() => setShowAudioModal(false)}
          onAudioReady={handleAudioRecorded}
          isFromConv={true}
          onOpenQuickAudioModals={() => {
            setShowAudioModal(false);
            setQuickFileSelectionType("audio");
            setShowQuickAlbumsModal(true);
          }}
        />

        {showQuickAlbumsModal && (
          <SelectQuickFileAlbumModal
            onClose={() => setShowQuickAlbumsModal(false)}
            onAlbumSelect={(albumId, type) => {
              setSelectedQuickAlbumId(albumId);
              setSelectedAlbumType(type);
              setShowQuickAlbumsModal(false);
            }}
            hideAudioTab={quickFileSelectionType === "media"}
            hideMediaTab={quickFileSelectionType === "audio"}
          />
        )}

        {selectedQuickAlbumId && (
          <SelectQuickFileMediaModal
            albumId={selectedQuickAlbumId}
            type={quickFileSelectionType === "audio" ? "audio" : "media"}
            onClose={() => {
              setSelectedQuickAlbumId(null);
              setSelectedAlbumType("media");
            }}
            onConfirm={handleQuickFilesConfirm}
            onBack={() => {
              setSelectedQuickAlbumId(null);
              setSelectedAlbumType("media");
              setShowQuickAlbumsModal(true);
            }}
          />
        )}
      </Layout>
    </Spin>
  );
};

export default CreatorConversationPage;
